import { useDraggable } from "@dnd-kit/core";
import styled from "styled-components";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import CottageIcon from "@mui/icons-material/Cottage";
import { ITripItem } from "@interfaces";
import { isOvernightStay } from "src/Utilities/tripPlannerHelpers";

const Card = styled.div<{ $dragging: boolean; $status: ITripItem["availabilityStatus"] }>`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 8px;
    border-radius: 6px;
    background: #fff;
    border: 1px solid
        ${(p) =>
            p.$status === "unavailable"
                ? "#e57373"
                : p.$status === "available"
                  ? "#81c784"
                  : "#e0e0e0"};
    cursor: grab;
    user-select: none;
    opacity: ${(p) => (p.$dragging ? 0.5 : 1)};
    font-size: 12px;
    position: relative;
    min-width: 0;
`;

const Thumb = styled.div<{ $src?: string }>`
    width: 32px;
    height: 32px;
    border-radius: 4px;
    flex-shrink: 0;
    background: #eee center/cover no-repeat ${(p) => (p.$src ? `url(${p.$src})` : "")};
`;

const Body = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 0;
    flex: 1;
`;

const Name = styled.div`
    font-weight: 600;
    /* Wrap long product names onto multiple lines instead of truncating, so
       the tile keeps a fixed width and never pushes its day column wider. */
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: break-word;
`;

const Meta = styled.div`
    color: #666;
    font-size: 11px;
`;

interface Props {
    item: ITripItem;
    onRemove: (id: string) => void;
    onClick?: (id: string) => void;
}

const StatusIcon = ({ status }: { status: ITripItem["availabilityStatus"] }) => {
    if (status === "checking") return <HourglassTopIcon sx={{ fontSize: 14, color: "#888" }} />;
    if (status === "available") return <CheckCircleIcon sx={{ fontSize: 14, color: "#43a047" }} />;
    if (status === "unavailable") return <WarningAmberIcon sx={{ fontSize: 14, color: "#e53935" }} />;
    return null;
};

export const TripItemCard = ({ item, onRemove, onClick }: Props) => {
    const { attributes, listeners, setNodeRef, isDragging, transform } = useDraggable({
        id: `item:${item.id}`,
        data: { kind: "item", itemId: item.id },
    });
    const style = transform
        ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
        : undefined;

    return (
        <Card
            ref={setNodeRef}
            $dragging={isDragging}
            $status={item.availabilityStatus}
            style={style}
            {...listeners}
            {...attributes}
            // Pointer sensor with distance:5 lets pure clicks (no drag) bubble
            // a click event; we forward that to onClick to open the editor.
            onClick={(e) => {
                if (isDragging) return;
                e.stopPropagation();
                onClick?.(item.id);
            }}
            title={item.availabilityMessage || item.name}
        >
            <Thumb $src={item.productImagePath} />
            <Body>
                <Name>{item.name}</Name>
                <Meta>
                    {item.quantity} × ${item.rrp.toFixed(0)}
                    {item.durationDays > 1 ? ` · ${item.durationDays} nights` : ""}
                </Meta>
            </Body>
            {isOvernightStay(item) && (
                <CottageIcon
                    data-testid="overnight-stay-icon"
                    sx={{ fontSize: 18, color: "#5d4037", flexShrink: 0 }}
                />
            )}
            <StatusIcon status={item.availabilityStatus} />
            <IconButton
                size="small"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                    e.stopPropagation();
                    onRemove(item.id);
                }}
                sx={{ p: 0.25 }}
            >
                <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
        </Card>
    );
};
